import { Timestamp } from 'firebase/firestore';

// Utility functions for handling Firestore timestamps and dates

export const convertFirestoreTimestamp = (timestamp: any): Date => {
  try {
    // Handle Firestore Timestamp objects
    if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    // Handle regular Date objects
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    // Handle timestamp numbers (milliseconds)
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    
    // Handle string dates
    if (typeof timestamp === 'string') {
      const parsed = new Date(timestamp);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    
    // Handle objects with seconds/nanoseconds (Firestore timestamp format)
    if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
      const seconds = timestamp.seconds || 0;
      const nanoseconds = timestamp.nanoseconds || 0;
      return new Date(seconds * 1000 + nanoseconds / 1000000);
    }
    
    // Fallback to current date
    console.warn('Invalid timestamp format:', timestamp);
    return new Date();
  } catch (error) {
    console.error('Error converting timestamp:', error, timestamp);
    return new Date();
  }
};

export const convertToFirestoreTimestamp = (date: Date | string | number): any => {
  try {
    if (date instanceof Date) {
      return Timestamp.fromDate(date);
    }
    
    if (typeof date === 'string') {
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) {
        return Timestamp.fromDate(parsed);
      }
    }
    
    if (typeof date === 'number') {
      return Timestamp.fromMillis(date);
    }
    
    console.warn('Invalid date format for Firestore:', date);
    return Timestamp.fromDate(new Date());
  } catch (error) {
    console.error('Error converting to Firestore timestamp:', error, date);
    return Timestamp.fromDate(new Date());
  }
};

export const formatDateForDisplay = (date: Date): string => {
  try {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error, date);
    return 'Invalid Date';
  }
};

export const formatDateTimeForDisplay = (date: Date): string => {
  try {
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting datetime:', error, date);
    return 'Invalid Date';
  }
};

export const isValidDate = (date: any): boolean => {
  try {
    if (!date) return false;
    
    if (date instanceof Date) {
      return !isNaN(date.getTime());
    }
    
    if (typeof date === 'string') {
      const parsed = new Date(date);
      return !isNaN(parsed.getTime());
    }
    
    if (typeof date === 'number') {
      const parsed = new Date(date);
      return !isNaN(parsed.getTime());
    }
    
    // Check for Firestore timestamp objects
    if (date && typeof date === 'object' && 'seconds' in date) {
      return true;
    }
    
    return false;
  } catch (error) {
    return false;
  }
}; 